import {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  ChangeEvent,
  MutableRefObject,
} from 'react'
import { useHistory, useLocation } from 'react-router'
import styled from 'styled-components'
import debounce from 'lodash/debounce'
import { v4 as uuid } from 'uuid'

import useDispatch from 'hooks/useDispatch'
import useDocumentClick from 'hooks/useDocumentClick'
import { useGetSearchBarResultsLazyQuery } from 'hooks/useQueries'
import { searchTypes, updateFilters } from 'actions/searchActions'
import { deserializeSearchQuery } from 'actions/utils/search'

import { minSearchQueryLength } from '../constants'

import Input from './Input'
import Results from './Results'

type LocationFilters = { country: string; state?: string }

interface SearchBarProps {
  mobile?: boolean
}

const Wrapper = styled.div<{ $loading?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  position: relative;
  border-radius: 6px;

  background-color: ${({ $loading, theme }) => {
    if ($loading) {
      return theme['-ds-neutral-300'].value
    }

    return theme['-ds-neutral-100'].value
  }};
`

const useFocusInputOnOpen = (
  searchInputRef: MutableRefObject<HTMLInputElement>,
  open: boolean,
) => {
  useEffect(() => {
    if (open) {
      searchInputRef.current?.focus()
    }
  }, [open, searchInputRef])
}

type LocationState = {
  primaryCityName: string
}

interface SearchBarProps {
  mobile?: boolean
}

const SearchBar = ({ mobile }: SearchBarProps) => {
  const dispatch = useDispatch()
  const location = useLocation<LocationState>()
  const history = useHistory()

  const queryStringFilters = deserializeSearchQuery(location.search)

  // SSR renders an empty wrapper; reveal the input only after mount so the
  // first client render matches SSR and hydration stays clean (Rollbar #16554).
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])

  const [open, setOpen] = useState(false)
  const [filters, setFilters] = useState(queryStringFilters)
  const [query, setQuery] = useState('')

  const debouncedEvent = useCallback(
    debounce((data, _query) => {
      analytics.track('Search by Title', {
        _query,
        resultsCount: data.searchByTitle?.edges?.length,
      })
    }, 1500),
    [],
  )

  const [sessionToken, setSessionToken] = useState(null)

  // What the displayed results were fetched with: place lookups must use this
  // token (not the live one) so Google can tie the lookup to its session, and
  // a query mismatch means the results on screen are stale.
  const [fetched, setFetched] = useState<{
    query: string
    sessionToken: string
  } | null>(null)

  const openRef = useRef(false)

  useEffect(() => {
    setSessionToken(uuid())
  }, [])

  const [
    callSearchBarResults,
    { data: searchBarResults, loading: searchBarResultsLoading },
  ] = useGetSearchBarResultsLazyQuery()

  const combinedResults = searchBarResults?.searchByTitle || []

  const resultsAreStale = fetched?.query !== query

  const searchInputRef = useRef<HTMLInputElement>()

  const outsideClickBoundary = useRef<HTMLDivElement>()

  const fetchAllResults = useMemo(
    () =>
      debounce((value: string): void => {
        setFetched({ query: value, sessionToken })
        callSearchBarResults({
          variables: { query: value, sessionToken },
        })
          .then(({ data }) => {
            if (data && openRef.current) debouncedEvent(data, value)
          })
          .catch((): void => {
            /* ignore search-bar lookup failures; they are non-critical */
          })
      }, 300),
    [sessionToken],
  )

  // Drop any pending debounced query when the token rotates (a new debounced
  // instance is created) or on unmount, so it can't fire with a stale token.
  useEffect(() => () => fetchAllResults.cancel(), [fetchAllResults])

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    setQuery(value || '')

    // Each keystroke costs a Google prediction request server-side, so wait
    // for a term long enough to produce useful results.
    if (value.trim().length >= minSearchQueryLength) {
      fetchAllResults(value)
    } else {
      fetchAllResults.cancel()
    }
  }

  const onRestoreFilters = () => {
    setFilters(queryStringFilters)
  }

  const onOpen = () => {
    // A Google autocomplete session token must not outlive one search
    // interaction; start a fresh one whenever the search is opened.
    if (!open) {
      setSessionToken(uuid())
    }
    openRef.current = true
    setOpen(true)
  }

  const onClose = () => {
    fetchAllResults.cancel()
    openRef.current = false
    setOpen(false)
  }

  const onClear = () => {
    fetchAllResults.cancel()
    setQuery('')
    setSessionToken(uuid())
    onRestoreFilters()
    searchInputRef.current?.focus()
  }

  const onShortcutSelect = ({ country, state = 'all' }: LocationFilters) => {
    history.push(
      `/listings?type=accommodation&country=${country}&state=${state}`,
    )

    dispatch(
      updateFilters({
        ne: null,
        sw: null,
      }),
    )

    onClose()
  }

  const inputPlaceholder = () => {
    const activeCountry = filters?.country

    const activeState = filters.state

    const activeType = searchTypes.find((type) => type.slug === filters.type)

    let placeholder = 'Search location or title'

    if ((activeType || activeState || activeCountry) && !open && !filters.q) {
      if (filters?.location) {
        placeholder = filters.location.toString()
      } else if (activeState) {
        placeholder = activeState.toString()
      } else if (location.state?.primaryCityName) {
        placeholder = `Near ${location.state.primaryCityName}`
      } else if (activeCountry) {
        placeholder = `All ${activeCountry}`
      } else {
        placeholder = 'Anywhere'
      }
    }

    return placeholder
  }

  const closeOnOutsideClick = (e: Event) => {
    if (
      outsideClickBoundary.current &&
      !outsideClickBoundary.current.contains(e.target as Node)
    ) {
      onClose()
    }
  }

  useDocumentClick(closeOnOutsideClick)

  useFocusInputOnOpen(searchInputRef, open)

  useEffect(() => {
    const newFilters = deserializeSearchQuery(location.search)
    setFilters(newFilters)
  }, [location.search])

  if (!hydrated) {
    return <Wrapper $loading />
  }

  return (
    <Wrapper $loading={false} ref={outsideClickBoundary}>
      <Input
        open={open}
        mobile={mobile}
        query={query}
        onChange={onChange}
        onClear={onClear}
        onClose={onClose}
        onOpen={onOpen}
        searchInputRef={searchInputRef}
        placeholder={inputPlaceholder()}
      />

      {open && (
        <Results
          mobile={mobile}
          query={query}
          results={combinedResults}
          loading={searchBarResultsLoading || resultsAreStale}
          onShortcutSelect={onShortcutSelect}
          onClose={onClose}
          sessionToken={fetched?.sessionToken}
        />
      )}
    </Wrapper>
  )
}

export default SearchBar
